import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  UserCircle2,
  UserMinus,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { BilingualText } from "../components/ui/BilingualText";
import { PageHeader } from "../components/ui/PageHeader";
import { EntityDrawer } from "../components/ui/EntityDrawer";
import { api, ApiError } from "../lib/api";
import type {
  BranchSummary,
  DepartmentSummary,
  GradeDetail,
  JobSummary,
  PositionCreateBody,
  PositionListItem,
} from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

// Position list + "New Position" dialog (M10.6).
// LE id from ?le=<uuid> query string (same convention as OrgChartPage,
// until M11 lands the LE picker). Detail editing happens via inline
// PATCH calls — clicking a row opens a side panel with editable fields.

function statusTone(s: string): "green" | "amber" | "slate" | "rose" {
  switch (s) {
    case "filled":
      return "green";
    case "open":
      return "amber";
    case "draft":
      return "slate";
    case "abolished":
      return "rose";
    default:
      return "slate";
  }
}

// eslint-disable-next-line react-refresh/only-export-components
function NewDepartmentInline({
  legalEntityId,
  onCreated,
  onCancel,
}: {
  legalEntityId: string;
  onCreated: (id: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [nameEn, setNameEn] = useState("");
  const [nameAr, setNameAr] = useState("");
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      api.createDepartment(legalEntityId, { name_en: nameEn, name_ar: nameAr }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["departments", legalEntityId] });
      onCreated(d.id);
    },
  });

  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {t("position.new_department")}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          autoFocus
          type="text"
          value={nameEn}
          onChange={(e) => setNameEn(e.target.value)}
          placeholder={t("position.name_en_placeholder")}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        />
        <input
          type="text"
          dir="rtl"
          value={nameAr}
          onChange={(e) => setNameAr(e.target.value)}
          placeholder={t("position.name_ar_placeholder")}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        />
      </div>
      {mutation.error && (
        <p className="mt-2 text-xs text-rose-700">
          {mutation.error instanceof ApiError
            ? mutation.error.detail
            : String(mutation.error)}
        </p>
      )}
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-slate-600 hover:text-slate-900"
        >
          {t("position.cancel")}
        </button>
        <button
          type="button"
          disabled={!nameEn || !nameAr || mutation.isPending}
          onClick={() => mutation.mutate()}
          className="rounded bg-brand px-3 py-1 text-xs font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
        >
          {mutation.isPending ? t("position.adding") : t("position.add")}
        </button>
      </div>
    </div>
  );
}

function NewPositionDialog({
  legalEntityId,
  jobs,
  departments,
  branches,
  grades,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  jobs: JobSummary[];
  departments: DepartmentSummary[];
  branches: BranchSummary[];
  grades: GradeDetail[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [jobId, setJobId] = useState<string>(jobs[0]?.id ?? "");
  const [departmentId, setDepartmentId] = useState<string>(departments[0]?.id ?? "");
  const [branchId, setBranchId] = useState<string>("");
  const [gradeId, setGradeId] = useState<string>("");
  const [titleEn, setTitleEn] = useState<string>("");
  const [titleAr, setTitleAr] = useState<string>("");
  const [showInlineDept, setShowInlineDept] = useState(false);

  const selectedJob = jobs.find((j) => j.id === jobId);

  // Auto-fill title from Job when a Job is first selected and the user
  // hasn't typed anything yet. Guarded so it doesn't fight user input.
  useEffect(() => {
    if (selectedJob && titleEn === "" && titleAr === "") {
      setTitleEn(selectedJob.title_en);
      setTitleAr(selectedJob.title_ar);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Keep dept selection in sync when departments list changes (after
  // an inline create lands a new row).
  useEffect(() => {
    if (!departmentId && departments.length > 0) {
      setDepartmentId(departments[0]!.id);
    }
  }, [departments, departmentId]);

  const mutation = useMutation({
    mutationFn: (body: PositionCreateBody) => api.createPosition(body),
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!jobId || !departmentId || !titleEn || !titleAr) return;
    const body: PositionCreateBody = {
      legal_entity_id: legalEntityId,
      job_id: jobId,
      department_id: departmentId,
      title_en: titleEn,
      title_ar: titleAr,
    };
    if (branchId) body.branch_id = branchId;
    if (gradeId) body.grade_id = gradeId;
    mutation.mutate(body);
  };

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("position.new_position")}
      description={t("position.new_position_description")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("position.cancel")}
          </button>
          <button
            type="submit"
            form="new-position-form"
            disabled={mutation.isPending || departments.length === 0}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover disabled:opacity-50"
          >
            {mutation.isPending
              ? t("position.creating")
              : t("position.create")}
          </button>
        </div>
      }
    >
        <form id="new-position-form" onSubmit={submit} className="space-y-4">
          <label className="block text-sm">
            <span className="font-medium text-slate-700">
              {t("position.job_template")}
            </span>
            <select
              required
              value={jobId}
              onChange={(e) => {
                setJobId(e.target.value);
                setTitleEn("");
                setTitleAr("");
              }}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.code} — {j.title_en}
                </option>
              ))}
            </select>
            {selectedJob && (
              <span className="mt-1 block text-xs text-slate-500">
                FTE {selectedJob.full_time_equivalent} ·{" "}
                {selectedJob.job_family ?? "no family"}
              </span>
            )}
          </label>

          <div className="block text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium text-slate-700">
                {t("position.department")}
              </span>
              {!showInlineDept && (
                <button
                  type="button"
                  onClick={() => setShowInlineDept(true)}
                  className="text-xs text-slate-600 hover:text-slate-900"
                >
                  {t("position.add_new_department")}
                </button>
              )}
            </div>
            {departments.length === 0 && !showInlineDept ? (
              <div className="mt-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t("position.no_departments_yet")}{" "}
                <button
                  type="button"
                  onClick={() => setShowInlineDept(true)}
                  className="font-semibold underline"
                >
                  {t("position.add_first_department")}
                </button>
              </div>
            ) : (
              <select
                required
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                disabled={departments.length === 0}
              >
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name_en}
                  </option>
                ))}
              </select>
            )}
            {showInlineDept && (
              <div className="mt-2">
                <NewDepartmentInline
                  legalEntityId={legalEntityId}
                  onCreated={(id) => {
                    setDepartmentId(id);
                    setShowInlineDept(false);
                  }}
                  onCancel={() => setShowInlineDept(false)}
                />
              </div>
            )}
          </div>

          {branches.length > 0 && (
            <label className="block text-sm">
              <span className="font-medium text-slate-700">
                {t("position.branch_optional")}
              </span>
              <select
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">{t("position.none_option")}</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name_en}
                    {b.city ? ` (${b.city})` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}

          {grades.length > 0 && (
            <label className="block text-sm">
              <span className="font-medium text-slate-700">
                {t("position.grade_optional")}
              </span>
              <select
                value={gradeId}
                onChange={(e) => setGradeId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">{t("position.none_option")}</option>
                {grades.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.code} — {g.name_en}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="font-medium text-slate-700">
                {t("position.title_en")}
              </span>
              <input
                required
                type="text"
                value={titleEn}
                onChange={(e) => setTitleEn(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700">
                {t("position.title_ar")}
              </span>
              <input
                required
                type="text"
                dir="rtl"
                value={titleAr}
                onChange={(e) => setTitleAr(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
          </div>

          {mutation.error && (
            <p className="rounded bg-rose-50 p-2 text-sm text-rose-700">
              {mutation.error instanceof ApiError
                ? mutation.error.detail
                : String(mutation.error)}
            </p>
          )}
        </form>
    </EntityDrawer>
  );
}

function PositionRow({ p }: { p: PositionListItem }) {
  const { t } = useTranslation();
  return (
    <tr className="hover:bg-slate-50">
      <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-slate-600 tabular-nums">
        <Link
          to={`/people/positions/${p.id}`}
          className="hover:underline"
        >
          {p.position_number_display}
        </Link>
      </td>
      <td className="px-4 py-3 text-sm font-medium text-slate-900">
        <Link to={`/people/positions/${p.id}`} className="hover:underline">
          <BilingualText
            en={p.title_en}
            ar={p.title_ar}
            primaryClassName="text-slate-900"
            secondaryClassName="text-xs text-slate-500"
          />
        </Link>
      </td>
      <td className="px-4 py-3 text-sm text-slate-700">
        <div className="font-medium">{p.job_title_en}</div>
        <div className="text-xs text-slate-500">{p.job_code}</div>
      </td>
      <td className="px-4 py-3 text-sm text-slate-700">{p.department_name_en}</td>
      <td className="px-4 py-3 text-sm">
        <Badge tone={statusTone(p.status)}>{p.status}</Badge>
      </td>
      <td className="px-4 py-3 text-sm text-slate-700">
        {p.current_assignee ? (
          <span className="inline-flex items-center gap-1.5">
            <UserCircle2 className="h-4 w-4 text-emerald-600" />
            {p.current_assignee.first_name_en} {p.current_assignee.last_name_en}
            <span className="text-xs text-slate-400">
              ({p.current_assignee.employee_code})
            </span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 italic text-slate-500">
            <UserMinus className="h-4 w-4 text-amber-500" />{" "}
            {t("position.vacant")}
          </span>
        )}
      </td>
    </tr>
  );
}

export function PositionsPage() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();

  const [statusFilter, setStatusFilter] = useState<string>("");
  const [showNew, setShowNew] = useState(false);
  const qc = useQueryClient();

  const positionsQ = useQuery({
    queryKey: ["positions", le, statusFilter],
    queryFn: () =>
      api.listPositions(le, statusFilter ? { status: statusFilter } : {}),
    enabled: !!le,
  });

  const jobsQ = useQuery({
    queryKey: ["jobs", le],
    queryFn: () => api.listJobs(le),
    enabled: !!le,
  });

  const departmentsQ = useQuery({
    queryKey: ["departments", le],
    queryFn: () => api.listDepartments(le),
    enabled: !!le,
  });

  const branchesQ = useQuery({
    queryKey: ["branches", le],
    queryFn: () => api.listBranches(le),
    enabled: !!le,
  });

  const gradesQ = useQuery({
    queryKey: ["grades", le],
    queryFn: () => api.listGrades(le),
    enabled: !!le,
  });

  if (gate) return gate;
  if (positionsQ.isLoading || jobsQ.isLoading) return <Loading />;
  if (positionsQ.error)
    return <ErrorPanel message={(positionsQ.error as Error).message} />;
  if (jobsQ.error)
    return <ErrorPanel message={(jobsQ.error as Error).message} />;

  const positions = positionsQ.data ?? [];
  const jobs = jobsQ.data ?? [];
  const departments = departmentsQ.data ?? [];
  const branches = branchesQ.data ?? [];
  const grades = gradesQ.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t("position.page_title")}
        subtitle={`(${positions.length})`}
      />
      <div className="flex items-center justify-between">
        <div /> {/* keep filters right-aligned */}
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
          >
            <option value="">{t("position.filter_all_statuses")}</option>
            <option value="draft">{t("position.status_draft")}</option>
            <option value="open">{t("position.status_open")}</option>
            <option value="filled">{t("position.status_filled")}</option>
            <option value="abolished">
              {t("position.status_abolished")}
            </option>
          </select>
          <button
            type="button"
            onClick={() => setShowNew(true)}
            disabled={jobs.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> {t("position.new_position")}
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-100">
          <thead className="bg-slate-50">
            <tr className="text-start text-xs font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 text-start">
                {t("position.col_number")}
              </th>
              <th className="px-4 py-3 text-start">
                {t("position.col_title")}
              </th>
              <th className="px-4 py-3 text-start">
                {t("position.col_job")}
              </th>
              <th className="px-4 py-3 text-start">
                {t("position.col_department")}
              </th>
              <th className="px-4 py-3 text-start">
                {t("position.col_status")}
              </th>
              <th className="px-4 py-3 text-start">
                {t("position.col_current_worker")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {positions.map((p) => (
              <PositionRow key={p.id} p={p} />
            ))}
            {positions.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-12 text-center text-sm text-slate-500"
                >
                  {t("position.no_positions_match_filter")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewPositionDialog
          legalEntityId={le}
          jobs={jobs}
          departments={departments}
          branches={branches}
          grades={grades}
          onClose={() => setShowNew(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ["positions"] });
            qc.invalidateQueries({ queryKey: ["org-chart"] });
          }}
        />
      )}
    </div>
  );
}
